import {
  Box,
  Spinner,
  Title,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { AbsencesStepDataDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { LevelItem } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/LevelItem'
import { absenceCodes } from 'modules/payroll_integrations_datev_lauds/components/SettingsSteps/Steps/helper'
import { DatevWizardData } from 'modules/payroll_integrations_datev_lauds/lib/types'

const AbsencesStep = ({ data, onDataChanged }: WizardStepRenderParams<DatevWizardData>) => {
  const {
    company: { id: companyId },
    access: { id: accessId },
  } = useCurrentSession()

  const { data: leaveTypesData, loading } = useQuery(AbsencesStepDataDocument, {
    variables: {
      companyId,
    },
  })

  const leaveTypes = leaveTypesData?.timeoff.leaveTypesConnection.nodes ?? []

  useOnNext(() => {
    const leaveTypesKeys = Object.keys(data.answers).filter((key) => /^leave_type_\d+$/.test(key))
    const mappedLeaveTypesCount = leaveTypesKeys.map((key) => !!data.answers[key]).length

    ;(track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsSettingsLeaveTypesMapped),
      {
        company_id: companyId,
        access_id: accessId,
        mapped_employee_groups: mappedLeaveTypesCount,
      })
  })

  const leaveTypesOptions = absenceCodes(data)

  const findLeavetype = (leaveTypeId: number | string) => {
    const payrollSoftware = data.answers.payroll_software

    const selection =
      payrollSoftware === 'datev_lodas'
        ? data.answers[`leave_type_${leaveTypeId}`]
        : data.answers[`leave_type_${leaveTypeId}`]
    if (!selection) return ''

    return leaveTypesOptions.find((option) => option.value === selection)?.label || ''
  }

  const onLeaveTypeMappingChanged = (leaveTypeId: string | number, value: string) => {
    onDataChanged({
      ...data,
      answers: {
        ...data.answers,
        [`leave_type_${leaveTypeId}`]: value,
      },
    })
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <Box flexDirection="row" gap="s96">
      <Box gap="s24">
        <Title
          title={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.absences.title'
          )}
          description={i18n.t(
            'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.absences.description'
          )}
        />
        <Box justifyContent="flexStart" flexDirection="column" gap="s32">
          {leaveTypes.map((type) => (
            <LevelItem
              key={`leave_type_${type.id}`}
              name={type.translatedName ?? ''}
              selectedValue={findLeavetype(type.id)}
              options={leaveTypesOptions}
              onSelect={(value, _label) => onLeaveTypeMappingChanged(type.id, value)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export const absencesStep = (): WizardStep<DatevWizardData> => ({
  title: i18n.t(
    'app_store_v2.app_integrations.payroll_integrations_datev_lauds.settings.steps.absences.wizard_title'
  ),
  nextText: i18n.t('common.next'),
  render: (props) => <AbsencesStep {...props} />,
  isCompleted: (_data) => true,
})
